import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchMarkers,
  upsertMarker as apiUpsertMarker,
  deleteMarker as apiDeleteMarker,
  SCHEDULER_KEYS,
} from "../api/schedulerApi";
import type { MarkerInput } from "../api/schedulerApi";

/* ── Hook ──────────────────────────────────────────────────────────── */

export function useMarkers(from?: string, to?: string) {
  const queryClient = useQueryClient();

  const markersQuery = useQuery({
    queryKey: from && to
      ? SCHEDULER_KEYS.markersByRange(from, to)
      : SCHEDULER_KEYS.markers(),
    queryFn: () => fetchMarkers(from, to),
    staleTime: 30_000,
  });

  const markers = markersQuery.data?.items ?? [];

  const upsertMutation = useMutation({
    mutationFn: (input: MarkerInput) => apiUpsertMarker(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.markers() });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDeleteMarker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHEDULER_KEYS.markers() });
    },
  });

  const saveMarker = useCallback(
    (input: MarkerInput) => upsertMutation.mutate(input),
    [upsertMutation],
  );

  const removeMarker = useCallback(
    (id: string) => deleteMutation.mutate(id),
    [deleteMutation],
  );

  return {
    markers,
    loading: markersQuery.isLoading,
    saving: upsertMutation.isPending || deleteMutation.isPending,
    saveMarker,
    removeMarker,
  } as const;
}
